import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/fonts/fonts.js';
import '@adornis/popover/x-tooltip';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { Language } from '@adornis/translation-core/languages.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import type { User } from '@dla/db/User.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { DlaTranslationDict } from '../translations.js';
import { DlaHelpDialog } from './dla-help-dialog.js';

@customElement('dla-login-nav')
export class DLALoginNav extends ChemistryLitElement {
  @state() private _currentUser = new RXController(this, currentUser<User>());
  @state() private _size = new WindowSizeController(this);

  private _translator = new TranslationController<DlaTranslationDict>(this);
  private _language = new RXController(this, globalLanguageSubject);

  private _currentUserView() {
    if (this._currentUser.value) {
      return html`
        <x-text>
          ${this._translator.translate('welcome')}, ${this._currentUser.value.name ?? this._currentUser.value.username}
        </x-text>
        ${this._helpButton()}
        <x-button
          ${css({
            scale: this._size.mobile ? 0.8 : undefined,
          })}
          tone="error"
          @click=${() => logout()}
        >
          ${this._translator.translate('logout')}
        </x-button>
      `;
    }
    return html`
      ${this._helpButton()}
      <x-button
        ${css({
          scale: this._size.mobile ? 0.8 : undefined,
        })}
        @click=${() => goTo('/login')}
      >
        ${this._translator.translate('login')}
      </x-button>
    `;
  }

  private _helpButton() {
    return html`
      <x-button
        mode="icon"
        icon="help"
        icon-outlined
        ${css({ color: this.colors.accent })}
        @click=${async () => {
          void DlaHelpDialog.showPopup({});
        }}
      >
        <x-tooltip> <x-text> ${this._translator.translate('help')} </x-text> </x-tooltip>
      </x-button>
    `;
  }

  protected override render() {
    if (
      routingState.value.searchParams.get('moodleMode') === '1' ||
      routingState.value.searchParams.get('minimal') === '1'
    ) {
      return nothing;
    }

    return html`
      <x-flex
        ${css({ backgroundColor: 'rgba(0,0,0,0.05)', height: this.sizes.topbar, padding: `0px ${this.spacing.md}` })}
        horizontal
        crossaxis-center
        space-between
      >
        <x-flex space="sm">
          <x-h3 @click=${() => goTo('/')} ${css({ cursor: 'pointer' })}> OctiPad </x-h3>
          <x-text ${css({ color: this.colors.neutral })}>
            ${this._translator.translate('collaborativeWriting')}
          </x-text>
        </x-flex>
        <x-flex horizontal crossaxis-center space="md">
          ${this._currentUserView()}
          <x-button
            mode="text"
            @click=${() => {
              globalLanguageSubject.next(this._language.value === Language.GERMAN ? Language.ENGLISH : Language.GERMAN);
            }}
          >
            <x-flex horizontal crossaxis-center>
              <x-text ?bold=${this._language.value === Language.GERMAN}> de </x-text> /
              <x-text ?bold=${this._language.value === Language.ENGLISH}> en </x-text>
            </x-flex>
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }
}
